import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  cloneRatePlanToDraft,
  getRatePlan,
  listRatePlans,
  listWorkspaceRatePlans,
  setPricingCatalogCurrency,
  siteCatalogCurrency,
  type RatePlanDetail,
  type RatePlanSummary,
  type WorkspacePinRow,
} from './api';
import PriceSheet from './PriceSheet';
import CollaboratorAiAllowance from './CollaboratorAiAllowance';
import RatePlanChange from './RatePlanChange';
import RatePlanVersions from './RatePlanVersions';
import PromoCodes from './PromoCodes';
import StoreProducts from './StoreProducts';

/**
 * Pricing.
 *
 * One screen, three states, in strict order of how often they are wanted:
 *
 *   1. What customers pay        -- the default, read-only, every visit
 *   2. Changing what they pay    -- a guided sequence, a few times a year
 *   3. Every list there has been -- the reference desk, behind a link
 *
 * The version this all hangs off is a database concept, not a task. It used to
 * be the first thing on the screen -- a left rail of "v1 GBP / v2 GBP / v1 EUR"
 * that had to be understood before anything else could be read. It is now
 * implicit: the screen opens on the live list, and the word "version" appears
 * only where it is genuinely load-bearing.
 */

type View = 'sheet' | 'change' | 'versions' | 'promos' | 'store';

export default function RatePlans({ toast }: { toast: (message: string) => void }) {
  const [plans, setPlans] = useState<RatePlanSummary[]>([]);
  const [pins, setPins] = useState<WorkspacePinRow[]>([]);
  const [siteCurrency, setSiteCurrency] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [view, setView] = useState<View>('sheet');
  /** Set only when looking at a list that is not the live one for this currency. */
  const [pinnedViewId, setPinnedViewId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RatePlanDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [newCurrency, setNewCurrency] = useState('');
  const [starting, setStarting] = useState(false);

  const loadPlans = useCallback(async () => {
    const rows = await listRatePlans();
    setPlans(rows);
    return rows;
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const [rows, site] = await Promise.all([loadPlans(), siteCatalogCurrency()]);
        setSiteCurrency(site);
        // Open on the currency the website is showing: that is the price list
        // the owner means when they say "our prices".
        const preferred = rows.find((p) => p.status === 'published' && p.currency === site)
          ?? rows.find((p) => p.status === 'published')
          ?? rows[0];
        setCurrency(preferred?.currency ?? null);
      } catch (e) {
        toast(`Could not load pricing: ${(e as Error).message}`);
      } finally {
        setLoading(false);
      }
      // Pins are only needed for the "how many customers are on each plan"
      // column, so a failure here must not stop the price sheet rendering.
      try {
        setPins(await listWorkspaceRatePlans());
      } catch {
        setPins([]);
      }
    })();
  }, [loadPlans, toast]);

  const currencies = useMemo(
    () => [...new Set(plans.map((p) => p.currency))].sort(),
    [plans],
  );
  const liveHere = useMemo(
    () => plans.find((p) => p.status === 'published' && p.currency === currency) ?? null,
    [plans, currency],
  );
  const draftHere = useMemo(
    () => plans.find((p) => p.status === 'draft' && p.currency === currency) ?? null,
    [plans, currency],
  );

  /** Which list the body is showing, decided by the view rather than a selector. */
  const activeId = useMemo(() => {
    if (view === 'change') return draftHere?.id ?? null;
    if (pinnedViewId) return pinnedViewId;
    return liveHere?.id ?? plans.find((p) => p.currency === currency)?.id ?? null;
  }, [view, draftHere, pinnedViewId, liveHere, plans, currency]);

  useEffect(() => {
    if (!activeId) { setDetail(null); return; }
    let alive = true;
    void getRatePlan(activeId)
      .then((next) => { if (alive) setDetail(next); })
      .catch((e: Error) => { if (alive) { setDetail(null); toast(`Could not read it: ${e.message}`); } });
    return () => { alive = false; };
  }, [activeId, toast]);

  const refresh = useCallback(async () => {
    await loadPlans();
    if (activeId) setDetail(await getRatePlan(activeId));
  }, [loadPlans, activeId]);

  // --- Starting a change -----------------------------------------------------

  const startChange = async () => {
    if (draftHere) { setPinnedViewId(null); setView('change'); return; }
    if (!liveHere) return toast('There is no live price list in this currency to change.');
    setStarting(true);
    try {
      await cloneRatePlanToDraft(liveHere.id, null, null);
      await loadPlans();
      setPinnedViewId(null);
      setView('change');
    } catch (e) {
      toast(`Could not start the change: ${(e as Error).message}`);
    } finally {
      setStarting(false);
    }
  };

  const addCurrency = async () => {
    const code = newCurrency.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) return toast('Use a three-letter code, such as EUR');
    if (currencies.includes(code)) return toast(`There is already a ${code} price list.`);
    const source = liveHere ?? plans.find((p) => p.status === 'published');
    if (!source) return toast('There is no live price list to copy from.');
    try {
      await cloneRatePlanToDraft(source.id, null, code);
      await loadPlans();
      setNewCurrency('');
      setCurrency(code);
      setPinnedViewId(null);
      setView('change');
      toast(`${code} started — every price was copied across as-is and needs setting for ${code}`);
    } catch (e) {
      toast(`Could not start ${code}: ${(e as Error).message}`);
    }
  };

  const presentOnSite = async (code: string) => {
    try {
      await setPricingCatalogCurrency(code);
      setSiteCurrency(await siteCatalogCurrency());
      toast(`The website now shows ${code} prices`);
    } catch (e) {
      toast(`Refused: ${(e as Error).message}`);
    }
  };

  const finishChange = async () => {
    await loadPlans();
    setPinnedViewId(null);
    setView('sheet');
  };

  // --- Render ----------------------------------------------------------------

  if (loading) return <p className="muted">Loading…</p>;

  if (!plans.length) {
    return (
      <div className="card">
        <p className="sub" style={{ margin: 0 }}>
          There are no price lists yet. One is created automatically from the existing billing
          catalog the first time the pricing migrations run.
        </p>
      </div>
    );
  }

  if (view === 'promos') {
    return <PromoCodes toast={toast} onBack={() => setView('sheet')} />;
  }

  if (view === 'store') {
    return (
      <StoreProducts
        plans={(detail?.editions ?? []).map((e) => ({ plan_key: e.plan_key, display_name: e.display_name }))}
        toast={toast}
        onBack={() => setView('sheet')}
      />
    );
  }

  if (view === 'versions') {
    return (
      <>
        <RatePlanVersions
          plans={plans}
          toast={toast}
          onBack={() => setView('sheet')}
          onShowPrices={(id) => {
            const row = plans.find((p) => p.id === id);
            if (row) setCurrency(row.currency);
            setPinnedViewId(row && row.status === 'published' ? null : id);
            setView('sheet');
          }}
        />
        <div className="card" style={{ marginTop: 18 }}>
          <h2 style={{ marginTop: 0 }}>Sell in another currency</h2>
          <p className="sub">
            Copies today&rsquo;s live prices into a new currency so they can be set for it. Nothing
            here knows an exchange rate, so every figure is copied across unchanged and has to be
            priced deliberately before it can go live.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input
              value={newCurrency}
              placeholder="EUR"
              onChange={(e) => setNewCurrency(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void addCurrency()}
              style={{ width: 90, textTransform: 'uppercase' }}
            />
            <button className="btn" onClick={() => void addCurrency()}>Start a price list</button>
            <span className="muted" style={{ fontSize: 12.5 }}>
              Already selling in {currencies.join(', ')}.
            </span>
          </div>
        </div>
      </>
    );
  }

  if (view === 'change') {
    if (!detail) return <p className="muted">Opening the change…</p>;
    return (
      <RatePlanChange
        plan={detail}
        liveVersion={liveHere?.version ?? null}
        onChanged={refresh}
        onDone={() => void finishChange()}
        onLeave={() => setView('sheet')}
        toast={toast}
      />
    );
  }

  return (
    <>
      <div
        className="row"
        style={{ gap: 8, marginBottom: 16, flexWrap: 'wrap', justifyContent: 'space-between' }}
      >
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {currencies.length > 1
            ? currencies.map((c) => (
                <button
                  key={c}
                  className={`chip ${c === currency ? 'active' : ''}`}
                  onClick={() => { setCurrency(c); setPinnedViewId(null); }}
                >
                  {c}
                  {c === siteCurrency ? ' · on the website' : ''}
                </button>
              ))
            : null}
          {pinnedViewId ? (
            <button className="chip" onClick={() => setPinnedViewId(null)}>
              ← Back to the live list
            </button>
          ) : null}
        </div>
        <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
          <button className="quiet" onClick={() => setView('promos')}>
            Promo codes
          </button>
          <button className="quiet" onClick={() => setView('store')}>
            Store products
          </button>
          <button className="quiet" onClick={() => setView('versions')}>
            Every price list there has been
          </button>
        </div>
      </div>

      {detail ? (
        <PriceSheet
          plan={detail}
          pins={pins}
          currencies={currencies}
          siteCurrency={siteCurrency}
          draftExists={!!draftHere}
          onPresentOnSite={(c) => void presentOnSite(c)}
          onChangePrices={() => void startChange()}
          onShowPromos={() => setView('promos')}
        />
      ) : (
        <p className="muted">{starting ? 'Starting the change…' : 'Loading the prices…'}</p>
      )}
      <CollaboratorAiAllowance toast={toast} />
    </>
  );
}
